<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const columns = [
  { key: 'date', label: t('LINE_REPORTS.TABLE.DATE') },
  { key: 'inbox_name', label: t('LINE_REPORTS.TABLE.BRAND') },
  { key: 'total', label: t('LINE_REPORTS.TABLE.TOTAL') },
  { key: 'reply_count', label: t('LINE_REPORTS.TABLE.REPLY_FREE') },
  { key: 'push_count', label: t('LINE_REPORTS.TABLE.PUSH_PAID') },
  { key: 'push_percentage', label: t('LINE_REPORTS.TABLE.PUSH_PERCENTAGE') },
  { key: 'top_admin', label: t('LINE_REPORTS.TABLE.TOP_ADMIN') },
];

const hasData = computed(() => (props.data || []).length > 0);

const formatTopAdmin = admin => {
  if (!admin) return '--';
  return `${admin.name} (${admin.push_count})`;
};
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow">
    <div class="flex items-center justify-between px-4 py-3">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ $t('LINE_REPORTS.TABLE.TITLE') }}
      </h3>
      <span v-if="isLoading" class="text-xs text-n-slate-11">
        {{ $t('LINE_REPORTS.LOADING') }}
      </span>
    </div>
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-n-weak">
        <thead class="bg-n-alpha-2">
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              scope="col"
              class="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak bg-transparent">
          <tr v-if="!isLoading && !hasData">
            <td
              :colspan="columns.length"
              class="px-4 py-6 text-center text-sm text-n-slate-11"
            >
              {{ $t('LINE_REPORTS.NO_DATA') }}
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="(row, index) in data"
              :key="`${row.date}-${row.inbox_id}-${index}`"
            >
              <td class="px-4 py-2 text-sm text-n-slate-12">
                {{ row.date }}
              </td>
              <td class="px-4 py-2 text-sm text-n-slate-12">
                {{ row.inbox_name }}
              </td>
              <td class="px-4 py-2 text-sm text-n-slate-12">
                {{ row.total }}
              </td>
              <td class="px-4 py-2 text-sm text-green-600 font-medium">
                {{ row.reply_count }}
              </td>
              <td class="px-4 py-2 text-sm text-red-600 font-medium">
                {{ row.push_count }}
              </td>
              <td class="px-4 py-2 text-sm text-n-slate-12">
                {{ row.push_percentage.toFixed(2) }}%
              </td>
              <td class="px-4 py-2 text-sm text-n-slate-12">
                {{ formatTopAdmin(row.top_admin) }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
